import React, { useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import IconProvider from "@/components/ui/IconProvider";

export interface RefreshButtonProps {
  onClick: () => void;
  isRefreshing?: boolean;
}

/**
 * Refresh button component.
 * Renders a refresh icon button for fetching the latest data.
 * Includes protection against double-clicks.
 */
export function RefreshButton({ onClick, isRefreshing = false }: RefreshButtonProps) {
  const isProcessingRef = useRef(false);

  const handleClick = useCallback(() => {
    // Prevent double-clicks
    if (isProcessingRef.current || isRefreshing) return;
    
    isProcessingRef.current = true;
    onClick();
    
    // Reset after a short delay to allow new clicks
    setTimeout(() => {
      isProcessingRef.current = false;
    }, 1000);
  }, [onClick, isRefreshing]);

  return (
    <Button
      variant="ghost"
      size="sm"
      className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted"
      onClick={handleClick}
      title="Refresh data"
      disabled={isRefreshing}
    >
      <IconProvider 
        iconstring="fa-refresh" 
        size={16} 
        color="currentColor"
        className={isRefreshing ? "animate-spin" : ""}
      />
    </Button>
  );
}

export default RefreshButton;
